import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SEO from '../components/SEO';
import './MassFinder.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Updated color scheme - refined, non-harsh
const AFFILIATION_COLORS = {
  'Diocesan': '#1e3a5f',      // Dark Blue
  'FSSP': '#1a365d',          // Navy
  'ICKSP': '#b8860b',         // Gold
  'Eastern Catholic': '#722f37', // Maroon
  'Ordinariate': '#2d5a4a',   // Forest Green
};

const AFFILIATION_ORDER = ['Diocesan', 'FSSP', 'ICKSP', 'Eastern Catholic', 'Ordinariate'];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Reliability based on location type
// Reliability - uses backend's reliability field if available, otherwise calculates from type
const getReliability = (location) => {
  // If location has explicit reliability field, use it
  if (location && location.reliability) {
    const level = location.reliability;
    const locType = (location.location_type || location.entity_type || '').toLowerCase();
    let label = 'Regular';
    if (locType.includes('parish') || locType.includes('priory')) label = 'Regular Parish';
    else if (locType.includes('chapel')) label = 'Chapel';
    else if (locType.includes('mission')) label = location.reliability_locked ? 'Established Mission' : 'Mission';
    else if (locType.includes('church')) label = 'Church';
    return { level, label };
  }
  
  // Fallback: calculate from location type string
  const locationType = typeof location === 'string' ? location : (location?.location_type || location?.entity_type || '');
  const type = (locationType || '').toLowerCase();
  if (type.includes('parish') || type.includes('priory') || type.includes('church')) {
    return { level: 'High', label: 'Regular Parish' };
  }
  if (type.includes('chapel')) {
    return { level: 'Medium', label: 'Chapel' };
  }
  if (type.includes('mission')) {
    return { level: 'Low', label: 'Mission' };
  }
  return { level: 'Medium', label: 'Regular' };
};

const createMarkerIcon = (color, isSelected = false) => {
  const size = isSelected ? 16 : 12;
  const svgIcon = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 1}" fill="${color}" stroke="white" stroke-width="2"/>
    </svg>
  `;
  return L.divIcon({
    html: svgIcon,
    className: `custom-marker ${isSelected ? 'selected' : ''}`,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
};

const createUserLocationIcon = () => {
  const svgIcon = `
    <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14" cy="14" r="12" fill="#1e3a5f" stroke="white" stroke-width="3"/>
      <circle cx="14" cy="14" r="4" fill="white"/>
    </svg>
  `;
  return L.divIcon({
    html: svgIcon,
    className: 'user-location-marker',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, zoom, { duration: 1 });
  }, [center, zoom, map]);
  return null;
};

const MassFinder = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filters, setFilters] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [activeAffiliation, setActiveAffiliation] = useState('');
  const [activeRite, setActiveRite] = useState('');
  const [activeState, setActiveState] = useState('');
  const [activeReliability, setActiveReliability] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Sort state
  const [sortBy, setSortBy] = useState('distance');
  
  // Map state
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]);
  const [mapZoom, setMapZoom] = useState(4);
  
  // Mobile UI state
  const [showMobileList, setShowMobileList] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  
  // Favorites state
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('massFinderFavorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  
  // Geolocation state
  const [userLocation, setUserLocation] = useState(null);
  const [nearbyMode, setNearbyMode] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [nearbyRadius, setNearbyRadius] = useState(100);
  
  // Suggestion modal state
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [suggestionType, setSuggestionType] = useState('new');
  const [suggestionSubmitting, setSuggestionSubmitting] = useState(false);
  const [suggestionSuccess, setSuggestionSuccess] = useState(false);
  const [suggestionError, setSuggestionError] = useState('');
  const [suggestionForm, setSuggestionForm] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    affiliation: 'Diocesan',
    rite: 'Latin',
    mass_schedule: '',
    website_url: '',
    phone: '',
    notes: '',
    reason: '',
    user_name: '',
    user_email: '',
    honeypot: ''
  });
  
  // Quick report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportLocation, setReportLocation] = useState(null);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportForm, setReportForm] = useState({
    issue_type: 'incorrect_info',
    description: '',
    user_email: ''
  });
  
  // Mass Times Verification state
  const [showTimesModal, setShowTimesModal] = useState(false);
  const [timesModalMode, setTimesModalMode] = useState('confirm'); // 'confirm' or 'suggest'
  const [timesSubmitting, setTimesSubmitting] = useState(false);
  const [timesSuccess, setTimesSuccess] = useState(false);
  const [timesError, setTimesError] = useState('');
  const [timesForm, setTimesForm] = useState({
    confirmed_accurate: true,
    user_notes: '',
    proposed_times: '',
    user_email: '',
    user_name: ''
  });

  useEffect(() => {
    fetchFilters();
    fetchStats();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (nearbyMode && userLocation) {
      fetchNearbyLocations();
    } else {
      fetchLocations();
    }
  }, [activeAffiliation, activeRite, activeState, searchQuery, nearbyMode, userLocation, nearbyRadius]);

  const fetchFilters = async () => {
    try {
      const response = await fetch(`${API}/mass-locations/filters`);
      if (response.ok) setFilters(await response.json());
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/mass-locations/stats`);
      if (response.ok) setStats(await response.json());
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      setLoading(true);
      let url = `${API}/mass-locations?`;
      const params = new URLSearchParams();
      
      if (activeAffiliation) params.append('affiliation', activeAffiliation);
      if (activeRite) params.append('rite', activeRite);
      if (activeState) params.append('state', activeState);
      if (searchQuery) {
        url = `${API}/mass-locations/search?`;
        params.append('q', searchQuery);
      }
      
      const response = await fetch(url + params.toString());
      if (response.ok) setLocations(await response.json());
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNearbyLocations = async () => {
    if (!userLocation) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('lat', userLocation.lat);
      params.append('lng', userLocation.lng);
      params.append('radius_miles', nearbyRadius);
      
      if (activeAffiliation) params.append('affiliation', activeAffiliation);
      if (activeRite) params.append('rite', activeRite);
      
      const response = await fetch(`${API}/mass-locations/search?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (error) {
      console.error('Error fetching nearby locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const findNearbyMasses = () => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser');
      return;
    }
    
    setGeoLoading(true);
    setGeoError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setMapCenter([latitude, longitude]);
        setMapZoom(9);
        setNearbyMode(true);
        setGeoLoading(false);
        setActiveState('');
        setSearchQuery('');
        setSearchInput('');
        setSortBy('distance');
      },
      (error) => {
        setGeoLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGeoError('Location access denied. Please enable location services.');
            break;
          case error.POSITION_UNAVAILABLE:
            setGeoError('Location information unavailable.');
            break;
          case error.TIMEOUT:
            setGeoError('Location request timed out.');
            break;
          default:
            setGeoError('An error occurred getting your location.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  const clearNearbyMode = () => {
    setNearbyMode(false);
    setUserLocation(null);
    setGeoError(null);
    setMapCenter([39.8283, -98.5795]);
    setMapZoom(4);
    fetchLocations();
  };

  // Favorites functions
  const toggleFavorite = (locationId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId];
      localStorage.setItem('massFinderFavorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  const isFavorite = (locationId) => favorites.includes(locationId);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  }, [searchInput]);

  const clearFilters = () => {
    setActiveAffiliation('');
    setActiveRite('');
    setActiveState('');
    setActiveReliability('');
    setSearchQuery('');
    setSearchInput('');
    setSelectedLocation(null);
    setNearbyMode(false);
    setUserLocation(null);
    setGeoError(null);
    setShowFavoritesOnly(false);
    setMapCenter([39.8283, -98.5795]);
    setMapZoom(4);
  };

  const selectLocation = (location) => {
    setSelectedLocation(location);
    setMapCenter([location.latitude, location.longitude]);
    setMapZoom(13);
    setShowMobileList(true);
  };

  const getMarkerColor = (affiliation) => AFFILIATION_COLORS[affiliation] || '#6b7280';

  // Suggestion handlers
  const openSuggestionModal = (type = 'new', location = null) => {
    setSuggestionType(type);
    setSuggestionSuccess(false);
    setSuggestionError('');
    
    if (type === 'edit' && location) {
      setSuggestionForm({
        name: location.name || '',
        street: location.street || '',
        city: location.city || '',
        state: location.state || '',
        zip_code: location.zip_code || '',
        country: location.country || 'USA',
        affiliation: location.affiliation || 'Diocesan',
        rite: location.rite || 'Latin',
        mass_schedule: '',
        website_url: location.website_url || '',
        phone: location.phone || '',
        notes: location.notes || '',
        reason: '',
        user_name: '',
        user_email: '',
        honeypot: '',
        location_id: location.id
      });
    } else {
      setSuggestionForm({
        name: '',
        street: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'USA',
        affiliation: 'Diocesan',
        rite: 'Latin',
        mass_schedule: '',
        website_url: '',
        phone: '',
        notes: '',
        reason: '',
        user_name: '',
        user_email: '',
        honeypot: ''
      });
    }
    setShowSuggestionModal(true);
  };

  const handleSuggestionChange = (e) => {
    const { name, value } = e.target;
    setSuggestionForm(prev => ({ ...prev, [name]: value }));
  };

  const submitSuggestion = async (e) => {
    e.preventDefault();
    setSuggestionSubmitting(true);
    setSuggestionError('');

    try {
      const payload = {
        suggestion_type: suggestionType,
        location_id: suggestionForm.location_id || null,
        user_email: suggestionForm.user_email,
        user_name: suggestionForm.user_name,
        name: suggestionForm.name,
        street: suggestionForm.street,
        city: suggestionForm.city,
        state: suggestionForm.state,
        zip_code: suggestionForm.zip_code,
        country: suggestionForm.country,
        affiliation: suggestionForm.affiliation,
        rite: suggestionForm.rite,
        mass_schedule: suggestionForm.mass_schedule,
        website_url: suggestionForm.website_url,
        phone: suggestionForm.phone,
        notes: suggestionForm.notes,
        reason: suggestionForm.reason,
        honeypot: suggestionForm.honeypot
      };

      const response = await fetch(`${API}/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuggestionSuccess(true);
      } else {
        const data = await response.json();
        setSuggestionError(data.detail || 'Failed to submit suggestion');
      }
    } catch (error) {
      setSuggestionError('An error occurred. Please try again.');
    } finally {
      setSuggestionSubmitting(false);
    }
  };

  const closeSuggestionModal = () => {
    setShowSuggestionModal(false);
    setSuggestionSuccess(false);
    setSuggestionError('');
  };

  // Quick report handlers
  const openReportModal = (location) => {
    setReportLocation(location);
    setReportSuccess(false);
    setReportError('');
    setReportForm({
      issue_type: 'incorrect_info',
      description: '',
      user_email: ''
    });
    setShowReportModal(true);
  };

  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReportForm(prev => ({ ...prev, [name]: value }));
  };

  const submitReport = async (e) => {
    e.preventDefault();
    setReportSubmitting(true);
    setReportError('');

    try {
      const issueLabels = {
        'incorrect_info': 'Incorrect Information',
        'closed': 'Location Closed/No Longer Exists',
        'wrong_times': 'Wrong Mass Times',
        'wrong_address': 'Wrong Address',
        'other': 'Other Issue'
      };

      const payload = {
        suggestion_type: 'edit',
        location_id: reportLocation.id,
        user_email: reportForm.user_email,
        name: reportLocation.name,
        city: reportLocation.city,
        state: reportLocation.state,
        country: reportLocation.country || 'USA',
        reason: `[${issueLabels[reportForm.issue_type]}] ${reportForm.description}`
      };

      const response = await fetch(`${API}/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setReportSuccess(true);
      } else {
        const data = await response.json();
        setReportError(data.detail || 'Failed to submit report');
      }
    } catch (error) {
      setReportError('An error occurred. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setReportSuccess(false);
    setReportError('');
  };

  // Mass Times Verification handlers
  const openTimesModal = (mode, location) => {
    setTimesModalMode(mode);
    setTimesSuccess(false);
    setTimesError('');
    setTimesForm({
      confirmed_accurate: mode === 'confirm',
      user_notes: '',
      proposed_times: location?.mass_schedule || '',
      user_email: '',
      user_name: ''
    });
    setShowTimesModal(true);
  };

  const closeTimesModal = () => {
    setShowTimesModal(false);
    setTimesSuccess(false);
    setTimesError('');
  };

  const handleTimesFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTimesForm(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const submitTimesConfirmation = async (e) => {
    e.preventDefault();
    if (!selectedLocation) return;
    
    setTimesSubmitting(true);
    setTimesError('');

    try {
      const response = await fetch(`${API}/mass-locations/${selectedLocation.id}/confirm-times`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location_id: selectedLocation.id,
          confirmed_accurate: timesForm.confirmed_accurate,
          user_notes: timesForm.user_notes
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTimesSuccess(true);
        // Update the selected location's verification data locally
        setSelectedLocation(prev => ({
          ...prev,
          times_verification: {
            ...prev.times_verification,
            confirmation_count: data.confirmation_count,
            confidence_score: data.confidence_score,
            last_confirmed: data.last_confirmed
          }
        }));
      } else {
        const data = await response.json();
        setTimesError(data.detail || 'Failed to submit confirmation');
      }
    } catch (error) {
      setTimesError('An error occurred. Please try again.');
    } finally {
      setTimesSubmitting(false);
    }
  };

  const submitTimesSuggestion = async (e) => {
    e.preventDefault();
    if (!selectedLocation) return;
    
    setTimesSubmitting(true);
    setTimesError('');

    try {
      const response = await fetch(`${API}/mass-locations/${selectedLocation.id}/suggest-times`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location_id: selectedLocation.id,
          proposed_times: timesForm.proposed_times,
          user_email: timesForm.user_email,
          user_name: timesForm.user_name,
          notes: timesForm.user_notes
        })
      });

      if (response.ok) {
        setTimesSuccess(true);
      } else {
        const data = await response.json();
        setTimesError(data.detail || 'Failed to submit suggestion');
      }
    } catch (error) {
      setTimesError('An error occurred. Please try again.');
    } finally {
      setTimesSubmitting(false);
    }
  };

  // Helper function to format "last confirmed" date
  const formatLastConfirmed = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
    return 'Over a year ago';
  };

  // Filter and sort locations
  const displayedLocations = useMemo(() => {
    // First filter out locations without valid coordinates
    let filtered = locations.filter(loc => 
      loc.latitude != null && 
      loc.longitude != null && 
      !isNaN(loc.latitude) && 
      !isNaN(loc.longitude)
    );
    
    if (showFavoritesOnly) {
      filtered = filtered.filter(loc => favorites.includes(loc.id));
    }
    
    if (activeReliability) {
      filtered = filtered.filter(loc => {
        const rel = getReliability(loc);
        return rel.level === activeReliability;
      });
    }
    
    // Sort
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          if (a.distance_miles && b.distance_miles) {
            return a.distance_miles - b.distance_miles;
          }
          return 0;
        case 'affiliation':
          return AFFILIATION_ORDER.indexOf(a.affiliation) - AFFILIATION_ORDER.indexOf(b.affiliation);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'reliability':
          const relA = getReliability(a).level;
          const relB = getReliability(b).level;
          const order = { 'High': 0, 'Medium': 1, 'Low': 2 };
          return order[relA] - order[relB];
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [locations, showFavoritesOnly, favorites, activeReliability, sortBy]);

  const hasActiveFilters = activeAffiliation || activeRite || activeState || activeReliability || searchQuery || nearbyMode || showFavoritesOnly;

  return (
    <div className="mass-finder-page" data-testid="mass-finder-page">
      <SEO 
        title="Traditional Latin Mass Finder | TLM, FSSP, ICKSP & Eastern Catholic Near You"
        description="Find Traditional Latin Mass (TLM), FSSP, ICKSP, Byzantine, Maronite, and reverent Novus Ordo parishes near you. The most complete Catholic Mass directory online."
        keywords="Latin Mass near me, Traditional Catholic Mass locator, FSSP parish, ICKSP chapel, Eastern Catholic, Byzantine Catholic, TLM finder, reverent Mass, traditional Latin mass near me"
        path="/mass-map"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://catholicvoicesandprayers.com/"},
                {"@type": "ListItem", "position": 2, "name": "Mass Finder", "item": "https://catholicvoicesandprayers.com/mass-map"}
              ]
            },
            {
              "@type": "WebApplication",
              "name": "Reverent Catholic Mass Finder",
              "description": "Find Traditional Latin Mass, Eastern Catholic, and reverent parishes near you across the United States and Canada.",
              "url": "https://catholicvoicesandprayers.com/mass-map",
              "applicationCategory": "ReligiousOrganization",
              "operatingSystem": "Web"
            }
          ]
        }}
      />
      
      {/* Hero Section */}
      <section className="finder-hero">
        <div className="finder-hero-content">
          <h1 className="finder-headline">Find a Reverent Catholic Mass Near You</h1>
          <p className="finder-subheadline">
            Search diocesan Latin Masses, Ecclesia Dei communities, Eastern Catholic parishes, and Ordinariate churches across North America.
          </p>
          
          {/* Hero Search */}
          <div className="finder-hero-search">
            <form className="hero-search-form" onSubmit={handleSearch}>
              <div className="hero-search-wrapper">
                <svg className="hero-search-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by city, state, or parish name..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="hero-search-input"
                  data-testid="finder-search-input"
                  disabled={nearbyMode}
                />
                {searchInput && (
                  <button type="button" className="hero-clear-btn" onClick={() => { setSearchInput(''); setSearchQuery(''); }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                )}
                <button type="submit" className="hero-search-btn" disabled={nearbyMode}>Search</button>
              </div>
            </form>
            
            {/* Find Nearest Mass - Primary CTA */}
            <button 
              className={`find-nearest-btn ${nearbyMode ? 'active' : ''}`}
              onClick={nearbyMode ? clearNearbyMode : findNearbyMasses}
              disabled={geoLoading}
              data-testid="find-nearest-btn"
            >
              {geoLoading ? (
                <>
                  <span className="btn-spinner"></span>
                  Locating...
                </>
              ) : nearbyMode ? (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                  Exit Nearby Mode
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
                  </svg>
                  Find the Nearest Mass
                </>
              )}
            </button>
          </div>
          
          {/* Geolocation Error */}
          {geoError && (
            <div className="geo-error-message" data-testid="geo-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span>{geoError}</span>
            </div>
          )}
          
          {/* Nearby Mode Controls */}
          {nearbyMode && (
            <div className="nearby-mode-controls" data-testid="nearby-controls">
              <div className="nearby-info-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span>Showing masses within <strong>{nearbyRadius} miles</strong></span>
              </div>
              <div className="radius-selector-wrapper">
                <label>Distance:</label>
                <select 
                  value={nearbyRadius} 
                  onChange={(e) => setNearbyRadius(Number(e.target.value))}
                  className="radius-select-input"
                  data-testid="radius-select"
                >
                  <option value={25}>25 miles</option>
                  <option value={50}>50 miles</option>
                  <option value={100}>100 miles</option>
                  <option value={250}>250 miles</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Map Legend - Above Map */}
      <section className="finder-legend-section">
        <div className="finder-container">
          <div className="legend-header">
            <h3 className="legend-title">Map Legend</h3>
            <Link to="/about-mass-finder" className="about-link">
              About This Directory
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Link>
          </div>
          <div className="legend-grid">
            {AFFILIATION_ORDER.map((name) => (
              <div 
                key={name} 
                className={`legend-item ${activeAffiliation === name ? 'legend-item-active' : ''}`}
                onClick={() => setActiveAffiliation(activeAffiliation === name ? '' : name)}
              >
                <span className="legend-marker" style={{ backgroundColor: AFFILIATION_COLORS[name] }} />
                <span className="legend-label">{name}</span>
                {stats?.by_affiliation[name] && (
                  <span className="legend-count">{stats.by_affiliation[name]}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters Bar */}
      <section className="finder-filters-bar">
        <div className="finder-container">
          <div className="filters-bar-content">
            <button 
              className={`filters-toggle-btn ${showFiltersPanel ? 'active' : ''}`}
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
              {hasActiveFilters && <span className="filter-count-badge">{[activeAffiliation, activeRite, activeState, activeReliability].filter(Boolean).length}</span>}
            </button>
            
            <div className="sort-wrapper">
              <label>Sort by:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
                <option value="distance">Distance</option>
                <option value="affiliation">Affiliation</option>
                <option value="name">Name</option>
                <option value="reliability">Reliability</option>
              </select>
            </div>
            
            <button 
              className={`favorites-btn ${showFavoritesOnly ? 'active' : ''}`}
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={showFavoritesOnly ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              Favorites {favorites.length > 0 && `(${favorites.length})`}
            </button>
            
            <button 
              className="suggest-btn"
              onClick={() => openSuggestionModal('new')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v8M8 12h8" />
              </svg>
              Suggest a Mass Location
            </button>
            
            {hasActiveFilters && (
              <button className="clear-all-btn" onClick={clearFilters}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                Clear All
              </button>
            )}
          </div>
          
          {/* Collapsible Filters Panel */}
          {showFiltersPanel && (
            <div className="filters-panel">
              <div className="filter-group">
                <label className="filter-label">Affiliation</label>
                <div className="filter-options">
                  <button 
                    className={`filter-option ${!activeAffiliation ? 'active' : ''}`}
                    onClick={() => setActiveAffiliation('')}
                  >
                    All
                  </button>
                  {AFFILIATION_ORDER.map(aff => (
                    <button 
                      key={aff}
                      className={`filter-option ${activeAffiliation === aff ? 'active' : ''}`}
                      onClick={() => setActiveAffiliation(aff)}
                    >
                      <span className="option-dot" style={{ backgroundColor: AFFILIATION_COLORS[aff] }} />
                      {aff}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="filter-group">
                <label className="filter-label">Rite</label>
                <select 
                  value={activeRite} 
                  onChange={(e) => setActiveRite(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Rites</option>
                  <option value="Latin">Latin</option>
                  <option value="Latin (1962)">Latin (1962)</option>
                  <option value="Byzantine">Byzantine</option>
                  <option value="Ukrainian">Ukrainian</option>
                  <option value="Maronite">Maronite</option>
                  <option value="Melkite">Melkite</option>
                  <option value="Chaldean">Chaldean</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label className="filter-label">State</label>
                <select 
                  value={activeState} 
                  onChange={(e) => setActiveState(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All States</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label className="filter-label">Reliability</label>
                <div className="filter-options">
                  <button 
                    className={`filter-option ${!activeReliability ? 'active' : ''}`}
                    onClick={() => setActiveReliability('')}
                  >
                    All
                  </button>
                  <button 
                    className={`filter-option ${activeReliability === 'High' ? 'active' : ''}`}
                    onClick={() => setActiveReliability('High')}
                  >
                    High
                  </button>
                  <button 
                    className={`filter-option ${activeReliability === 'Medium' ? 'active' : ''}`}
                    onClick={() => setActiveReliability('Medium')}
                  >
                    Medium
                  </button>
                  <button 
                    className={`filter-option ${activeReliability === 'Low' ? 'active' : ''}`}
                    onClick={() => setActiveReliability('Low')}
                  >
                    Low
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Map & Results */}
      <section className="finder-content">
        <div className="finder-container">
          <div className="finder-layout">
            {/* Map */}
            <div className="finder-map-wrapper">
              <MapContainer center={mapCenter} zoom={mapZoom} className="finder-map" scrollWheelZoom={true} zoomControl={true}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapController center={mapCenter} zoom={mapZoom} />
                
                {/* User location marker */}
                {userLocation && (
                  <Marker
                    position={[userLocation.lat, userLocation.lng]}
                    icon={createUserLocationIcon()}
                    zIndexOffset={1000}
                  >
                    <Popup>
                      <div className="finder-popup user-popup">
                        <h4>Your Location</h4>
                        <p>Showing masses within {nearbyRadius} miles</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
                
                {/* Location markers with clustering */}
                <MarkerClusterGroup
                  chunkedLoading
                  maxClusterRadius={50}
                  spiderfyOnMaxZoom={true}
                  showCoverageOnHover={false}
                >
                  {displayedLocations.map((loc) => (
                    <Marker
                      key={loc.id}
                      position={[loc.latitude, loc.longitude]}
                      icon={createMarkerIcon(getMarkerColor(loc.affiliation), selectedLocation?.id === loc.id)}
                      eventHandlers={{ click: () => selectLocation(loc) }}
                    >
                      <Popup>
                        <div className="finder-popup">
                          <div className="popup-header">
                            <h4>{loc.name}</h4>
                            <button 
                              className={`popup-fav-btn ${isFavorite(loc.id) ? 'is-favorite' : ''}`}
                              onClick={(e) => { e.stopPropagation(); toggleFavorite(loc.id); }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill={isFavorite(loc.id) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                              </svg>
                            </button>
                          </div>
                          <p className="popup-location">{loc.city}, {loc.state}</p>
                          <div className="popup-badges">
                            <span className="popup-badge" style={{ backgroundColor: getMarkerColor(loc.affiliation) }}>
                              {loc.affiliation}
                            </span>
                          </div>
                          {loc.distance_miles && (
                            <p className="popup-distance">{loc.distance_miles} miles away</p>
                          )}
                          <button className="popup-details-btn" onClick={() => selectLocation(loc)}>
                            View Details →
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              </MapContainer>

              <div className="map-results-badge">
                <span className="results-count">{displayedLocations.length}</span>
                <span className="results-label">{showFavoritesOnly ? 'favorites' : nearbyMode ? 'nearby' : 'locations'}</span>
              </div>
            </div>

            {/* Mobile List Toggle */}
            <button className="mobile-list-toggle" onClick={() => setShowMobileList(!showMobileList)}>
              {showMobileList ? 'Hide List' : 'Show List'} ({displayedLocations.length})
            </button>

            {/* Results Panel */}
            <div className={`finder-results-panel ${showMobileList ? 'show-mobile' : ''}`}>
              {selectedLocation ? (
                <div className="location-detail-view">
                  <button className="back-btn" onClick={() => setSelectedLocation(null)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    Back to Results
                  </button>

                  <div className="detail-header">
                    <div className="detail-badges">
                      <span className="detail-badge affiliation-badge" style={{ backgroundColor: getMarkerColor(selectedLocation.affiliation) }}>
                        {selectedLocation.affiliation}
                      </span>
                      <button 
                        className={`detail-fav-btn ${isFavorite(selectedLocation.id) ? 'is-favorite' : ''}`}
                        onClick={() => toggleFavorite(selectedLocation.id)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavorite(selectedLocation.id) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        {isFavorite(selectedLocation.id) ? 'Saved' : 'Save'}
                      </button>
                    </div>
                    <h2 className="detail-name">{selectedLocation.name}</h2>
                    <p className="detail-meta">
                      {selectedLocation.location_type || selectedLocation.entity_type || 'Parish'} • {selectedLocation.rite} Rite
                    </p>
                    
                    {/* Reliability Indicator */}
                    <div className="reliability-indicator">
                      {(() => {
                        const rel = getReliability(selectedLocation);
                        return (
                          <>
                            <span className={`reliability-badge reliability-${rel.level.toLowerCase()}`}>
                              Reliability: {rel.level}
                            </span>
                            <span className="reliability-label">({rel.label})</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="detail-content">
                    {/* Mass Times */}
                    <div className="detail-section mass-times-section">
                      <div className="section-header-row">
                        <h4 className="section-title">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 6v6l4 2" />
                          </svg>
                          Mass Times
                        </h4>
                        {/* Verification Status Badge */}
                        {selectedLocation.times_verification && (
                          <span className={`times-confidence-badge confidence-${selectedLocation.times_verification.confidence_level?.toLowerCase() || 'unverified'}`}>
                            {selectedLocation.times_verification.confidence_level || 'Unverified'}
                          </span>
                        )}
                      </div>
                      
                      {selectedLocation.mass_schedule ? (
                        <>
                          <p className="mass-schedule">{selectedLocation.mass_schedule}</p>
                          
                          {/* Verification Info */}
                          <div className="times-verification-info" data-testid="times-verification-info">
                            {selectedLocation.times_verification?.last_confirmed && (
                              <p className="last-confirmed-text">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M9 12l2 2 4-4" />
                                  <circle cx="12" cy="12" r="10" />
                                </svg>
                                Last confirmed: {formatLastConfirmed(selectedLocation.times_verification.last_confirmed)}
                                {selectedLocation.times_verification.confirmation_count > 0 && (
                                  <span className="confirm-count">
                                    ({selectedLocation.times_verification.confirmation_count} confirmation{selectedLocation.times_verification.confirmation_count !== 1 ? 's' : ''})
                                  </span>
                                )}
                              </p>
                            )}
                            {!selectedLocation.times_verification?.last_confirmed && (
                              <p className="unconfirmed-text">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="M12 8v4M12 16h.01" />
                                </svg>
                                Times not yet confirmed by community
                              </p>
                            )}
                          </div>
                          
                          {/* Verification Actions */}
                          <div className="times-verification-actions" data-testid="times-verification-actions">
                            <button 
                              className="times-action-btn confirm-btn"
                              onClick={() => openTimesModal('confirm', selectedLocation)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 12l2 2 4-4" />
                                <circle cx="12" cy="12" r="10" />
                              </svg>
                              Confirm These Times
                            </button>
                            <button 
                              className="times-action-btn suggest-btn"
                              onClick={() => openTimesModal('suggest', selectedLocation)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                              Suggest Correction
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="mass-unknown">
                          <p>Schedule not available</p>
                          <button className="submit-times-btn" onClick={() => openTimesModal('suggest', selectedLocation)}>
                            Know the times? Submit them →
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Address */}
                    <div className="detail-section">
                      <h4 className="section-title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        Address
                      </h4>
                      <p className="address-line">{selectedLocation.street || 'Address not available'}</p>
                      <p className="address-city">{selectedLocation.city}, {selectedLocation.state} {selectedLocation.zip_code}</p>
                      {selectedLocation.distance_miles && (
                        <p className="distance-info">{selectedLocation.distance_miles} miles from your location</p>
                      )}
                    </div>

                    {/* Notes */}
                    {selectedLocation.notes && (
                      <div className="detail-section">
                        <h4 className="section-title">Notes</h4>
                        <p className="notes-text">{selectedLocation.notes}</p>
                      </div>
                    )}
                  </div>

                  <div className="detail-actions">
                    {selectedLocation.website_url && (
                      <a href={selectedLocation.website_url} target="_blank" rel="noopener noreferrer" className="action-btn primary">
                        Parish Website
                      </a>
                    )}
                    <a 
                      href={`https://www.google.com/maps/dir/?api=1&destination=${selectedLocation.latitude},${selectedLocation.longitude}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="action-btn"
                    >
                      Get Directions
                    </a>
                    <button className="action-btn" onClick={() => openSuggestionModal('edit', selectedLocation)}>
                      Suggest Edit
                    </button>
                    <button className="action-btn report" onClick={() => openReportModal(selectedLocation)}>
                      Report Issue
                    </button>
                  </div>
                </div>
              ) : (
                <div className="results-list-view">
                  <div className="results-header">
                    <h3>{showFavoritesOnly ? 'My Favorites' : nearbyMode ? 'Nearby Masses' : 'All Locations'}</h3>
                    <span className="results-count-label">{displayedLocations.length} found</span>
                  </div>
                  
                  {loading ? (
                    <div className="loading-state">
                      <div className="loading-spinner" />
                      <span>Finding masses...</span>
                    </div>
                  ) : displayedLocations.length > 0 ? (
                    <div className="results-scroll">
                      {displayedLocations.map((loc) => {
                        const rel = getReliability(loc);
                        return (
                          <button key={loc.id} className="result-card" onClick={() => selectLocation(loc)}>
                            <div className="card-indicator" style={{ backgroundColor: getMarkerColor(loc.affiliation) }} />
                            <div className="card-body">
                              <div className="card-header">
                                <h4 className="card-name">{loc.name}</h4>
                                {isFavorite(loc.id) && (
                                  <svg className="card-fav-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                  </svg>
                                )}
                              </div>
                              <p className="card-location">
                                {loc.city}, {loc.state}
                                {loc.distance_miles && (
                                  <span className="card-distance"> • {loc.distance_miles} mi</span>
                                )}
                              </p>
                              <div className="card-tags">
                                <span className="card-tag" style={{ backgroundColor: getMarkerColor(loc.affiliation), color: 'white' }}>
                                  {loc.affiliation}
                                </span>
                                <span className="card-tag reliability-tag">
                                  {rel.level}
                                </span>
                              </div>
                              {loc.mass_schedule && (
                                <p className="card-schedule">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 6v6l4 2" />
                                  </svg>
                                  {loc.mass_schedule.length > 60 ? loc.mass_schedule.substring(0, 60) + '...' : loc.mass_schedule}
                                </p>
                              )}
                            </div>
                            <svg className="card-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M8 15h8M9 9h.01M15 9h.01" />
                      </svg>
                      <p>{showFavoritesOnly ? 'No favorites saved yet' : 'No locations found'}</p>
                      <span>{showFavoritesOnly ? 'Click the heart icon on any location to save it' : 'Try adjusting your filters or search terms'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      {stats && (
        <section className="finder-stats-section">
          <div className="finder-container">
            <h3 className="stats-section-title">Directory Overview</h3>
            <div className="stats-grid">
              <div className="stat-card total-card">
                <span className="stat-number">{stats.total}</span>
                <span className="stat-label">Total Locations</span>
              </div>
              {AFFILIATION_ORDER.map((name) => (
                stats.by_affiliation[name] && (
                  <div key={name} className="stat-card">
                    <span className="stat-number" style={{ color: AFFILIATION_COLORS[name] }}>
                      {stats.by_affiliation[name]}
                    </span>
                    <span className="stat-label">{name}</span>
                  </div>
                )
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Disclaimer Section */}
      <section className="finder-disclaimer-section">
        <div className="finder-container finder-container-narrow">
          <div className="disclaimer-content">
            <h4>About This Directory</h4>
            <p>
              The Reverent Catholic Mass Finder is a curated directory of Catholic Mass locations across North America. 
              It includes diocesan Latin Mass communities, FSSP and ICKSP parishes, Ordinariate communities, 
              and Eastern Catholic parishes.
            </p>
            <p>
              This directory does not include sedevacantist groups. All listings are reviewed for 
              Catholic sacramental validity.
            </p>
            <div className="disclaimer-links">
              <Link to="/about-mass-finder" className="disclaimer-link">
                Learn more about our inclusion criteria →
              </Link>
              <button className="disclaimer-link" onClick={() => openSuggestionModal('new')}>
                Suggest a correction or new location →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Suggestion Modal */}
      {showSuggestionModal && (
        <div className="modal-overlay" onClick={closeSuggestionModal} data-testid="suggestion-modal">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{suggestionType === 'new' ? 'Suggest a New Location' : 'Suggest an Edit'}</h3>
              <button className="modal-close-btn" onClick={closeSuggestionModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {suggestionSuccess ? (
              <div className="modal-success">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                <h4>Thank You!</h4>
                <p>Your suggestion has been submitted and marked as <strong>Pending Verification</strong>. We'll review it soon.</p>
                <button className="btn-primary" onClick={closeSuggestionModal}>Close</button>
              </div>
            ) : (
              <form onSubmit={submitSuggestion} className="modal-form">
                {suggestionError && (
                  <div className="form-error">{suggestionError}</div>
                )}

                <div className="form-section">
                  <h4>Your Information</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Your Name</label>
                      <input
                        type="text"
                        name="user_name"
                        value={suggestionForm.user_name}
                        onChange={handleSuggestionChange}
                        placeholder="John Smith"
                      />
                    </div>
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="user_email"
                        value={suggestionForm.user_email}
                        onChange={handleSuggestionChange}
                        required
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Location Details</h4>
                  <div className="form-group">
                    <label>Parish/Chapel Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={suggestionForm.name}
                      onChange={handleSuggestionChange}
                      required
                      placeholder="St. Mary's Church"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Affiliation *</label>
                      <select name="affiliation" value={suggestionForm.affiliation} onChange={handleSuggestionChange}>
                        <option value="Diocesan">Diocesan</option>
                        <option value="FSSP">FSSP</option>
                        <option value="ICKSP">ICKSP</option>
                        <option value="Ordinariate">Ordinariate</option>
                        <option value="Eastern Catholic">Eastern Catholic</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Rite</label>
                      <select name="rite" value={suggestionForm.rite} onChange={handleSuggestionChange}>
                        <option value="Latin">Latin</option>
                        <option value="Latin (1962)">Latin (1962)</option>
                        <option value="Byzantine">Byzantine</option>
                        <option value="Ukrainian">Ukrainian</option>
                        <option value="Maronite">Maronite</option>
                        <option value="Melkite">Melkite</option>
                        <option value="Chaldean">Chaldean</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Street Address</label>
                    <input
                      type="text"
                      name="street"
                      value={suggestionForm.street}
                      onChange={handleSuggestionChange}
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>City *</label>
                      <input
                        type="text"
                        name="city"
                        value={suggestionForm.city}
                        onChange={handleSuggestionChange}
                        required
                        placeholder="City"
                      />
                    </div>
                    <div className="form-group">
                      <label>State *</label>
                      <input
                        type="text"
                        name="state"
                        value={suggestionForm.state}
                        onChange={handleSuggestionChange}
                        required
                        placeholder="CA"
                        maxLength="2"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Mass Information</h4>
                  <div className="form-group">
                    <label>Mass Times</label>
                    <textarea
                      name="mass_schedule"
                      value={suggestionForm.mass_schedule}
                      onChange={handleSuggestionChange}
                      rows="2"
                      placeholder="Sunday: 10 AM High Mass, 12 PM Low Mass"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Website</label>
                      <input
                        type="url"
                        name="website_url"
                        value={suggestionForm.website_url}
                        onChange={handleSuggestionChange}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={suggestionForm.phone}
                        onChange={handleSuggestionChange}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Additional Notes</label>
                    <textarea
                      name="notes"
                      value={suggestionForm.notes}
                      onChange={handleSuggestionChange}
                      rows="2"
                      placeholder="Any other helpful information..."
                    />
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-group">
                    <label>{suggestionType === 'new' ? 'Why should this location be added?' : 'What needs to be changed?'}</label>
                    <textarea
                      name="reason"
                      value={suggestionForm.reason}
                      onChange={handleSuggestionChange}
                      rows="2"
                      placeholder={suggestionType === 'new' ? 'e.g., Recently opened Latin Mass community...' : 'e.g., Address is incorrect, new Mass times...'}
                    />
                  </div>
                </div>

                {/* Honeypot field */}
                <input
                  type="text"
                  name="honeypot"
                  value={suggestionForm.honeypot}
                  onChange={handleSuggestionChange}
                  style={{ display: 'none' }}
                  tabIndex="-1"
                  autoComplete="off"
                />

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeSuggestionModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={suggestionSubmitting}>
                    {suggestionSubmitting ? 'Submitting...' : 'Submit Suggestion'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && reportLocation && (
        <div className="modal-overlay" onClick={closeReportModal} data-testid="report-modal">
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Report an Issue</h3>
              <button className="modal-close-btn" onClick={closeReportModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="report-location-info">
              <strong>{reportLocation.name}</strong>
              <span>{reportLocation.city}, {reportLocation.state}</span>
            </div>

            {reportSuccess ? (
              <div className="modal-success">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                <h4>Thank You!</h4>
                <p>We've received your report and will review it soon.</p>
                <button className="btn-primary" onClick={closeReportModal}>Close</button>
              </div>
            ) : (
              <form onSubmit={submitReport} className="modal-form">
                {reportError && (
                  <div className="form-error">{reportError}</div>
                )}

                <div className="form-group">
                  <label>What's the issue? *</label>
                  <select 
                    name="issue_type" 
                    value={reportForm.issue_type} 
                    onChange={handleReportChange}
                    required
                  >
                    <option value="incorrect_info">Incorrect Information</option>
                    <option value="wrong_address">Wrong Address</option>
                    <option value="wrong_times">Wrong Mass Times</option>
                    <option value="closed">Location Closed/No Longer Exists</option>
                    <option value="other">Other Issue</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Please describe the issue *</label>
                  <textarea
                    name="description"
                    value={reportForm.description}
                    onChange={handleReportChange}
                    required
                    rows="3"
                    placeholder="What information is incorrect? What should it be changed to?"
                  />
                </div>

                <div className="form-group">
                  <label>Your Email (optional)</label>
                  <input
                    type="email"
                    name="user_email"
                    value={reportForm.user_email}
                    onChange={handleReportChange}
                    placeholder="In case we need to follow up"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeReportModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={reportSubmitting}>
                    {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Mass Times Verification Modal */}
      {showTimesModal && selectedLocation && (
        <div className="modal-overlay" onClick={closeTimesModal} data-testid="times-verification-modal">
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{timesModalMode === 'confirm' ? 'Confirm Mass Times' : 'Suggest Updated Times'}</h3>
              <button className="modal-close-btn" onClick={closeTimesModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="report-location-info">
              <strong>{selectedLocation.name}</strong>
              <span>{selectedLocation.city}, {selectedLocation.state}</span>
            </div>

            {timesSuccess ? (
              <div className="modal-success">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                <h4>Thank You!</h4>
                <p>
                  {timesModalMode === 'confirm' 
                    ? 'Your confirmation helps others trust this information!' 
                    : 'Your suggestion has been submitted for review.'}
                </p>
                <button className="btn-primary" onClick={closeTimesModal}>Close</button>
              </div>
            ) : timesModalMode === 'confirm' ? (
              <form onSubmit={submitTimesConfirmation} className="modal-form">
                {timesError && (
                  <div className="form-error">{timesError}</div>
                )}

                {selectedLocation.mass_schedule && (
                  <div className="current-times-display">
                    <label>Current Listed Times:</label>
                    <p className="times-text">{selectedLocation.mass_schedule}</p>
                  </div>
                )}

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="confirmed_accurate"
                      checked={timesForm.confirmed_accurate}
                      onChange={handleTimesFormChange}
                    />
                    <span>Yes, these times are accurate</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Additional notes (optional)</label>
                  <textarea
                    name="user_notes"
                    value={timesForm.user_notes}
                    onChange={handleTimesFormChange}
                    rows="2"
                    placeholder="e.g., Confession available 30 min before Mass"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeTimesModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={timesSubmitting}>
                    {timesSubmitting ? 'Submitting...' : 'Confirm Times'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={submitTimesSuggestion} className="modal-form">
                {timesError && (
                  <div className="form-error">{timesError}</div>
                )}

                {selectedLocation.mass_schedule && (
                  <div className="current-times-display">
                    <label>Current Listed Times:</label>
                    <p className="times-text">{selectedLocation.mass_schedule}</p>
                  </div>
                )}

                <div className="form-group">
                  <label>Correct Mass Times *</label>
                  <textarea
                    name="proposed_times"
                    value={timesForm.proposed_times}
                    onChange={handleTimesFormChange}
                    required
                    rows="3"
                    placeholder="e.g., Sunday: 10 AM High Mass, 12 PM Low Mass; Weekdays: 7 AM"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Your Name</label>
                    <input
                      type="text"
                      name="user_name"
                      value={timesForm.user_name}
                      onChange={handleTimesFormChange}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="user_email"
                      value={timesForm.user_email}
                      onChange={handleTimesFormChange}
                      required
                      placeholder="For follow-up if needed"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Additional notes (optional)</label>
                  <textarea
                    name="user_notes"
                    value={timesForm.user_notes}
                    onChange={handleTimesFormChange}
                    rows="2"
                    placeholder="e.g., Times changed after new pastor arrived"
                  />
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={closeTimesModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={timesSubmitting}>
                    {timesSubmitting ? 'Submitting...' : 'Submit Suggestion'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MassFinder;
